import {
  Avatar,
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDataProvider, ListBase, WithListContext, useTranslate } from 'react-admin';
import { Resources } from 'src/pages/admin/resources/resource';
import { PredictionMarketItem } from '../types';
import { useSearchMarketDialogContext } from './SearchMarketDialogContext';

type MarketsTabProps = {
  active: boolean;
};

const MARKETS_PAGE_SIZE = 15;

function MarketsTabContent({
  data,
  isLoading,
  isFetching,
  isPending,
  error,
  total,
  offset,
  onLoadMore,
  onLoadingChange,
}: {
  data?: PredictionMarketItem[];
  isLoading?: boolean;
  isFetching?: boolean;
  isPending?: boolean;
  error?: any;
  total?: number;
  offset: number;
  onLoadMore: () => void;
  onLoadingChange: (loading: boolean) => void;
}) {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const { state, actions } = useSearchMarketDialogContext();
  const [mergedResults, setMergedResults] = useState<PredictionMarketItem[]>([]);
  const currentPageResults = useMemo(() => data ?? [], [data]);
  const loading = Boolean(
    isLoading || isPending || (isFetching && offset === 0 && mergedResults.length === 0)
  );
  const isFetchingMore = Boolean(isFetching && offset > 0);
  const hasReliableTotal = typeof total === 'number' && total > mergedResults.length;
  const hasMore = hasReliableTotal
    ? mergedResults.length < total
    : currentPageResults.length >= MARKETS_PAGE_SIZE;

  useEffect(() => {
    if (offset === 0) {
      setMergedResults(currentPageResults);
      return;
    }

    if (currentPageResults.length === 0) {
      return;
    }

    setMergedResults((currentResults) => {
      const nextResults = [...currentResults];
      const existingIds = new Set(currentResults.map((item) => `${item.id}`));

      currentPageResults.forEach((item) => {
        if (!existingIds.has(`${item.id}`)) {
          nextResults.push(item);
        }
      });

      return nextResults;
    });
  }, [currentPageResults, offset]);

  useEffect(() => {
    onLoadingChange(loading || isFetchingMore);
  }, [isFetchingMore, loading, onLoadingChange]);

  useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  const results = mergedResults;

  const handleSelectMarket = async (id: number) => {
    const { data: dataEn } = await dataProvider.getOne(Resources.predictionFeaturedMarketsMarkets, {
      id,
      meta: { acceptLanguage: 'en' },
    });
    const { data: dataZh } = await dataProvider.getOne(Resources.predictionFeaturedMarketsMarkets, {
      id,
      meta: { acceptLanguage: 'zh' },
    });

    const marketItem = {
      ...dataEn,
      question: dataEn.question,
      questionZh: dataZh.question,
    };
    actions.selectMarket({ ...marketItem, type: 'MARKET' });
  };

  if (loading) return null;

  if (results.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {translate('module.featuredMarkets.noMarkets')}
      </Typography>
    );
  }

  return (
    <Box
      sx={{ maxHeight: 420, overflowY: 'auto' }}
      onScroll={(event) => {
        const element = event.currentTarget;
        const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 48;

        if (isNearBottom && hasMore && !isFetchingMore) {
          onLoadMore();
        }
      }}
    >
      <List sx={{ py: 0 }}>
        {results.map((item, index) => {
          const isAdded = state.selectedIds.has(`${item.id}`);

          return (
            <Box key={`${item.id}-${index}`}>
              <ListItemButton
                disabled={isAdded}
                onClick={() => handleSelectMarket(item.id)}
                sx={{ px: 0 }}
              >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                  <Avatar
                    src={item.icon || item.image}
                    alt={item.question}
                    variant="rounded"
                    sx={{ width: 40, height: 40 }}
                  />
                  <ListItemText
                    primary={item.question}
                    secondary={
                      isAdded
                        ? translate('module.featuredMarkets.alreadyAdded')
                        : item.slug || `ID: ${item.id}`
                    }
                  />
                </Stack>
              </ListItemButton>
              {index < results.length - 1 ? <Divider /> : null}
            </Box>
          );
        })}

        {isFetchingMore ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 0, py: 2 }}>
            {translate('module.featuredMarkets.loading')}
          </Typography>
        ) : null}
      </List>
    </Box>
  );
}

export default function MarketsTab({ active }: MarketsTabProps) {
  const { state } = useSearchMarketDialogContext();
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (active) {
      setOffset(0);
    }
  }, [active, state.searchValue]);

  if (!active) return null;

  return (
    <ListBase
      resource={Resources.predictionFeaturedMarketsMarkets}
      filter={{ q: state.searchValue, offset: `${offset}` }}
      perPage={MARKETS_PAGE_SIZE}
      queryOptions={{ retry: false }}
      sort={{ field: 'id', order: 'ASC' }}
      storeKey={false}
      disableSyncWithLocation
    >
      <WithListContext<PredictionMarketItem>
        render={(context) => (
          <MarketsTabContent
            {...context}
            offset={offset}
            onLoadMore={() => setOffset((currentOffset) => currentOffset + MARKETS_PAGE_SIZE)}
            onLoadingChange={() => {}}
          />
        )}
      />
    </ListBase>
  );
}
